import { useState } from 'react'
import { useCreateProduct } from '@/hooks/useProducts'
import type { CreatedProduct, ProductCatalogEntry } from '@/types/products'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { ProductCatalogPicker } from '@/components/procedure/ProductCatalogPicker'
import { cn } from '@/lib/utils'

/**
 * Product-selection popup opened from the "Create Product" (P) action on the
 * patient case page. Filters + single-select table, per the Create New
 * Product spec, then records the created product; the server mints its DIN
 * from the tenant's DIN settings (z8nrz7fxwh). The chosen
 * catalog entry rides along in `onCreated` because the storage-assignment
 * step that follows needs its storage profile.
 */
export function CreateProductDialog({
  open,
  onClose,
  submissionId,
  formDefinitionId,
  procedureId,
  patientMrn,
  patientName,
  patientDob,
  requiresPatientHistory,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  submissionId: string
  formDefinitionId: string
  procedureId: string
  patientMrn: string
  patientName: string | null
  patientDob: string | null
  requiresPatientHistory: boolean
  onCreated: (product: CreatedProduct, catalogEntry: ProductCatalogEntry) => void
}) {
  const createProduct = useCreateProduct()

  // Two steps in one dialog: choose the product code, THEN state the volume.
  // Volume sat under the picker before, competing with the create button on a
  // screen whose whole job is choosing a code — and it is asked for at all
  // because split, pool and volume updates every work from this figure.
  const [step, setStep] = useState<'pick' | 'volume'>('pick')
  const [selected, setSelected] = useState<ProductCatalogEntry | null>(null)
  const [volumeMl, setVolumeMl] = useState('')
  const [minting, setMinting] = useState(false)
  const [mintError, setMintError] = useState<string | null>(null)

  function handleOpenChange(next: boolean) {
    if (!next) {
      setStep('pick')
      setSelected(null)
      setVolumeMl('')
      setMintError(null)
      onClose()
    }
  }

  async function handleCreate() {
    if (!selected) return
    setMinting(true)
    setMintError(null)
    try {
      // No DIN: the server mints it from the tenant's sequence.
      const product = await createProduct.mutateAsync({
        catalogEntryId: selected.id,
        submissionId,
        formDefinitionId,
        procedureId,
        patientMrn: requiresPatientHistory ? patientMrn : null,
        patientName: requiresPatientHistory ? patientName : null,
        patientDob: requiresPatientHistory ? patientDob : null,
        volumeMl: volumeMl.trim() ? Number(volumeMl) : undefined,
      })
      const entry = selected
      handleOpenChange(false)
      onCreated(product, entry)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to create product.'
      setMintError(message)
    } finally {
      setMinting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/*
        The picker needs the full table width; the volume step is one field and
        looks abandoned at that size.

        Capped in height and scrollable, like the other wide dialogs here
        (ProductCodeDialog, BoardRunDialogs): the filter bar wraps to a second
        row on a narrow window, and without the cap that pushed the footer —
        Cancel and Create — off the bottom of the screen.
      */}
      <DialogContent
        className={cn(
          'max-h-[90vh] overflow-y-auto',
          // 80% of the viewport, not a fixed 1024px. The catalog table has
          // eleven columns and the picker is the one dialog in the app with
          // more to show than a laptop is wide — a fixed cap left a third of a
          // large monitor empty while the table scrolled sideways inside it.
          // The volume step is a single field and looks abandoned at that size,
          // so it keeps its own narrow cap.
          step === 'pick' ? 'sm:max-w-[80vw]' : 'sm:max-w-md',
        )}
      >
        <DialogHeader>
          <DialogTitle>Create product</DialogTitle>
          <DialogDescription>
            {step === 'pick'
              ? 'Use the filters below to select the product code applicable for your process. Select the desired product code and click create.'
              : 'How much is in the bag? Split, pool and volume updates all work from this figure.'}
          </DialogDescription>
        </DialogHeader>

        {/*
          The picker stays MOUNTED on the volume step, only hidden: it owns five
          filter dropdowns, and unmounting it would silently clear them every
          time someone stepped Back to change their mind.

          `min-w-0` is load-bearing. DialogContent is a GRID, and a grid item
          defaults to `min-width: auto` — it refuses to shrink below its
          content's intrinsic width. The catalog table gives every column an
          explicit `minWidth` (ResizableTableHead), so without this the track
          stretched to the full table width, dragging the whole dialog past its
          width cap and off the right of the screen, taking the Create button
          with it. The table's own `overflow-auto` could not help, because that
          container was itself being stretched.
        */}
        <div className={cn('min-w-0', step === 'pick' ? undefined : 'hidden')}>
          <ProductCatalogPicker selected={selected} onSelect={setSelected} />
        </div>

        {step === 'pick' ? (
          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={() => setStep('volume')} disabled={!selected}>
              + Create New Product
            </Button>
          </div>
        ) : (
          <>
            <div className='rounded-lg border bg-muted/20 px-3 py-2 text-sm'>
              <span className='text-muted-foreground'>Product code: </span>
              <span className='font-mono font-medium'>
                {selected?.productDescriptionCode || '—'}
              </span>
              <span className='text-muted-foreground'>
                {' '}
                · {selected?.productClass}
                {selected?.storageTemperature ? ` · ${selected.storageTemperature}` : ''}
              </span>
            </div>

            <div className='space-y-1'>
              <label className='text-sm font-medium' htmlFor='create-product-volume'>
                Volume (mL)
              </label>
              <Input
                id='create-product-volume'
                type='number'
                min={0}
                value={volumeMl}
                onChange={e => setVolumeMl(e.target.value)}
                placeholder='mL'
                className='max-w-[12rem]'
                autoFocus
              />
              <p className='text-xs text-muted-foreground'>
                Leave blank if it isn't known yet — it can be set later from Manage Products.
              </p>
            </div>

            {mintError && <p className='text-sm text-destructive'>{mintError}</p>}

            <div className='flex justify-end gap-2'>
              <Button variant='outline' onClick={() => setStep('pick')} disabled={minting}>
                Back
              </Button>
              <Button onClick={handleCreate} disabled={minting}>
                {minting ? 'Creating…' : 'Create product'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
